import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  Search, Bookmark, Sparkles, Upload, ShieldCheck, Download, ArrowRight,
  Check, X, Wand2, Captions, Ratio,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1490810194309-344b3661ba39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const navigate = useNavigate();

  const freeFeatures = [
    "Browse & search campaigns", "View campaign requirements", "See payouts & deadlines",
    "Import campaigns by URL", "Save to your library", "Compare & prepare campaigns",
  ];
  const paidFeatures = [
    "Upload & analyse footage", "AI clip candidate generation", "Auto captions & reframing",
    "Watermarks & text overlays", "Campaign compliance checks", "Export finished clips",
  ];
  const steps = [
    { icon: Search, t: "Find a Campaign", d: "Discover clipping campaigns across platforms — free." },
    { icon: Bookmark, t: "Save Campaign", d: "Build a library of campaigns you want to clip." },
    { icon: Sparkles, t: "Unlock Clipping", d: "Subscribe to access the AI clipping tools." },
    { icon: Upload, t: "Upload Video", d: "Drop your footage or paste a video URL." },
    { icon: Wand2, t: "Generate Clips", d: "AI finds moments and renders campaign-ready clips." },
    { icon: ShieldCheck, t: "Check Compliance", d: "Every clip is scanned against the rules." },
    { icon: Download, t: "Download", d: "Export compliant clips and post them." },
  ];

  return (
    <div className="min-h-screen grain">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 via-zinc-950/90 to-zinc-950" />
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8 fade-up">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-widest text-amber-300">
              Universal AI Clipping Layer
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] fade-up">
            Find any clipping campaign.<br />
            <span className="text-amber-400">Let Clipstar do the clipping.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed fade-up">
            One AI clipping tool for campaigns across Whop, CLIPPING and more. Discover and prepare
            campaigns for free — then upload your footage and let AI generate compliant clips.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3 fade-up">
            <Button
              onClick={() => navigate("/register")}
              data-testid="hero-get-started"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold h-12 px-8 rounded-full text-base amber-glow hover:-translate-y-0.5 transition-transform"
            >
              Start Free — Browse Campaigns <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              onClick={() => navigate("/pricing")}
              data-testid="hero-pricing"
              variant="outline"
              className="h-12 px-8 rounded-full border-white/15 hover:bg-white/5 text-base"
            >
              See Pricing
            </Button>
          </div>
          <div className="mt-12 flex items-center justify-center gap-6 flex-wrap text-zinc-500 text-sm fade-up">
            {["Whop", "CLIPPING", "YouTube", "TikTok", "Instagram"].map((p) => (
              <span key={p} className="font-mono uppercase tracking-wider">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Free vs Paid */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold">Free to discover. Paid to create.</h2>
          <p className="mt-3 text-zinc-400">The business model, made obvious.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#141419] border border-emerald-500/20 rounded-2xl p-8">
            <div className="inline-flex px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-mono uppercase tracking-wider mb-5">
              Free Discovery
            </div>
            <h3 className="font-display text-xl font-bold mb-1">Explore & save campaigns</h3>
            <p className="text-zinc-500 text-sm mb-6">“What campaigns can I clip?”</p>
            <ul className="space-y-3">
              {freeFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-zinc-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#141419] border border-amber-500/30 rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="inline-flex px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-xs font-mono uppercase tracking-wider mb-5">
              Paid Pro Tools
            </div>
            <h3 className="font-display text-xl font-bold mb-1">Let AI make the clips</h3>
            <p className="text-zinc-500 text-sm mb-6">“Can Clipstar actually make the clips for me?”</p>
            <ul className="space-y-3">
              {paidFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-zinc-200">
                  <Sparkles className="w-4 h-4 text-amber-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-12">How it works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-[#141419] border border-white/10 rounded-2xl p-6 hover:border-amber-500/30 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-amber-400" />
                </div>
                <span className="font-mono text-xs text-zinc-600">0{i + 1}</span>
              </div>
              <h4 className="font-display font-semibold mb-1">{s.t}</h4>
              <p className="text-sm text-zinc-500 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <div className="bg-gradient-to-br from-amber-500/10 to-violet-500/10 border border-white/10 rounded-3xl p-12">
          <h2 className="font-display text-3xl font-bold mb-3">Start clipping smarter</h2>
          <p className="text-zinc-400 mb-8">Free to browse. No card needed to explore campaigns.</p>
          <Button
            onClick={() => navigate("/register")}
            data-testid="cta-signup"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold h-12 px-8 rounded-full amber-glow"
          >
            Create Free Account
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
